import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner, toast } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/components/ThemeProvider";
import { useSessionTracking } from "@/hooks/useSessionTracking";
import { AuthGuard, AuthProvider } from "@/components/auth/AuthGuard";

import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { OfflineIndicator } from "@/components/pwa/OfflineIndicator";
import { UpdateBanner } from "@/components/pwa/UpdateBanner";
import { UpdateSplashScreen } from "@/components/pwa/UpdateSplashScreen";
import { useAppVersion } from "@/hooks/useAppVersion";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";

// Critical pages loaded eagerly
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy-loaded pages for performance
const UnifiedAuth = lazy(() => import("./pages/auth/UnifiedAuth"));
const PatientAuth = lazy(() => import("./pages/auth/PatientAuth"));
const DoctorAuth = lazy(() => import("./pages/auth/DoctorAuth"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DoctorDashboard = lazy(() => import("./pages/doctor/DoctorDashboard"));
const Doctors = lazy(() => import("./pages/Doctors"));
const DoctorProfile = lazy(() => import("./pages/DoctorProfile"));
const Credits = lazy(() => import("./pages/Credits"));
const Consultation = lazy(() => import("./pages/Consultation"));
const Diary = lazy(() => import("./pages/Diary"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const DoctorTerms = lazy(() => import("./pages/DoctorTerms"));
const TelemedicineCompliance = lazy(() => import("./pages/TelemedicineCompliance"));
const Admin = lazy(() => import("./pages/Admin"));
const AccountantDashboard = lazy(() => import("./pages/AccountantDashboard"));
const InvestorDashboard = lazy(() => import("./pages/InvestorDashboard"));
const Support = lazy(() => import("./pages/Support"));
const Profile = lazy(() => import("./pages/Profile"));
const ConsultationHistory = lazy(() => import("./pages/ConsultationHistory"));
const Install = lazy(() => import("./pages/Install"));
const Investigations = lazy(() => import("./pages/Investigations"));
const RatingTransparency = lazy(() => import("./pages/RatingTransparency"));
const WalletTerms = lazy(() => import("./pages/WalletTerms"));
const PharmacyDashboard = lazy(() => import("./pages/pharmacy/PharmacyDashboard"));
const LabDashboard = lazy(() => import("./pages/lab/LabDashboard"));
const PartnerAuth = lazy(() => import("./pages/auth/PartnerAuth"));
const Labs = lazy(() => import("./pages/Labs"));
const Pharmacies = lazy(() => import("./pages/Pharmacies"));
const SpecialityIndex = lazy(() => import("./pages/seo/SpecialityIndex"));
const CityDoctors = lazy(() => import("./pages/seo/CityDoctors"));
const ConditionPage = lazy(() => import("./pages/seo/ConditionPage"));
const HealthFeed = lazy(() => import("./pages/HealthFeed"));
const TopicPage = lazy(() => import("./pages/seo/TopicPage"));
const MarketingDashboard = lazy(() => import("./pages/marketing/MarketingDashboard"));
const MarketingBenefits = lazy(() => import("./pages/marketing/MarketingBenefits"));
const PresentationExport = lazy(() => import("./pages/PresentationExport"));
const PatientProfileSetup = lazy(() => import("./pages/patient/ProfileSetup"));
const AssociationDoctors = lazy(() => import("./pages/AssociationDoctors"));
const DoctorShortLink = lazy(() => import("./pages/DoctorShortLink"));
const Features = lazy(() => import("./pages/Features"));

const queryClient = new QueryClient();

// Redirect /register?ref=CODE → /auth/doctor?ref=CODE (persists ref in localStorage)
const RegisterRedirect = () => {
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  if (ref) {
    localStorage.setItem("ilajly_doctor_ref", ref.toUpperCase());
  }
  return <Navigate to={`/auth/doctor${ref ? `?ref=${ref}` : ""}`} replace />;
};

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground text-sm">Loading…</div>
  </div>
);

const AppContent = () => {
  useSessionTracking();
  const { updateAvailable, justUpdated, performUpdate, dismissUpdateSuccess, currentVersion, updating, updateStage, updateProgress, updateSuccess } = useAppVersion();

  useEffect(() => {
    if (justUpdated) {
      toast.success("iLAJLY Updated Successfully", {
        description: `Your app is now running the latest version${currentVersion ? ` (${currentVersion})` : ""}.`,
        duration: 6000,
      });
      dismissUpdateSuccess();
    }
  }, [justUpdated, currentVersion, dismissUpdateSuccess]);

  return (
    <>
      <Toaster />
      <Sonner />
      <MaintenanceBanner />
      <UpdateBanner visible={updateAvailable && !updating} onUpdate={performUpdate} />
      <UpdateSplashScreen visible={updating} stage={updateStage} progress={updateProgress} success={updateSuccess} />
      <OfflineIndicator />
      <InstallPrompt />
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/register" element={<RegisterRedirect />} />
            <Route path="/auth" element={<UnifiedAuth />} />
            <Route path="/auth/login" element={<UnifiedAuth />} />
            <Route path="/auth/patient" element={<PatientAuth />} />
            <Route path="/auth/doctor" element={<DoctorAuth />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/patient/profile-setup" element={<AuthGuard><PatientProfileSetup /></AuthGuard>} />
            <Route path="/dashboard" element={<AuthGuard><Dashboard /></AuthGuard>} />
            <Route path="/doctor/dashboard" element={<AuthGuard><DoctorDashboard /></AuthGuard>} />
            <Route path="/doctors" element={<Doctors />} />
            <Route path="/doctor/:slug" element={<DoctorProfile />} />
            <Route path="/dr/:slug" element={<DoctorShortLink />} />
            <Route path="/credits" element={<AuthGuard><Credits /></AuthGuard>} />
            <Route path="/diary" element={<AuthGuard><Diary /></AuthGuard>} />
            <Route path="/consultation/:id" element={<AuthGuard><Consultation /></AuthGuard>} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/doctor-terms" element={<DoctorTerms />} />
            <Route path="/telemedicine-compliance" element={<TelemedicineCompliance />} />
            <Route path="/admin" element={<AuthGuard><Admin /></AuthGuard>} />
            <Route path="/accountant" element={<AuthGuard><AccountantDashboard /></AuthGuard>} />
            <Route path="/investor" element={<InvestorDashboard />} />
            <Route path="/support" element={<Support />} />
            <Route path="/profile" element={<AuthGuard><Profile /></AuthGuard>} />
            <Route path="/consultations" element={<AuthGuard><ConsultationHistory /></AuthGuard>} />
            <Route path="/install" element={<Install />} />
            <Route path="/investigations" element={<AuthGuard><Investigations /></AuthGuard>} />
            <Route path="/how-ratings-work" element={<RatingTransparency />} />
            <Route path="/wallet-terms" element={<WalletTerms />} />
            <Route path="/auth/partner/:type" element={<PartnerAuth />} />
            <Route path="/pharmacy/dashboard" element={<AuthGuard><PharmacyDashboard /></AuthGuard>} />
            <Route path="/lab/dashboard" element={<AuthGuard><LabDashboard /></AuthGuard>} />
            <Route path="/labs" element={<Labs />} />
            <Route path="/pharmacies" element={<Pharmacies />} />
            <Route path="/health-feed" element={<HealthFeed />} />
            <Route path="/topic/:tag" element={<TopicPage />} />
            <Route path="/topic/:tag/:lang" element={<TopicPage />} />
            <Route path="/marketing/dashboard" element={<AuthGuard><MarketingDashboard /></AuthGuard>} />
            <Route path="/marketing/benefits" element={<MarketingBenefits />} />
            <Route path="/presentation" element={<PresentationExport />} />
            <Route path="/association/:id" element={<AssociationDoctors />} />
            <Route path="/features" element={<Features />} />
            {/* SEO Pages */}
            <Route path="/city/:city/:speciality" element={<CityDoctors />} />
            <Route path="/homeopathy-doctors" element={<SpecialityIndex />} />
            <Route path="/ayurveda-doctors" element={<SpecialityIndex />} />
            <Route path="/siddha-doctors" element={<SpecialityIndex />} />
            <Route path="/unani-doctors" element={<SpecialityIndex />} />
            <Route path="/yoga-naturopathy-doctors" element={<SpecialityIndex />} />
            <Route path="/homeopathy-treatment-for-migraine" element={<ConditionPage />} />
            <Route path="/homeopathy-treatment-for-asthma" element={<ConditionPage />} />
            <Route path="/homeopathy-treatment-for-eczema" element={<ConditionPage />} />
            <Route path="/homeopathy-treatment-for-anxiety" element={<ConditionPage />} />
            <Route path="/ayurveda-treatment-for-arthritis" element={<ConditionPage />} />
            <Route path="/ayurveda-treatment-for-pcos" element={<ConditionPage />} />
            <Route path="/ayurveda-treatment-for-diabetes" element={<ConditionPage />} />
            <Route path="/ayurveda-treatment-for-acidity" element={<ConditionPage />} />
            <Route path="/unani-treatment-for-diabetes" element={<ConditionPage />} />
            <Route path="/unani-treatment-for-skin-disease" element={<ConditionPage />} />
            <Route path="/siddha-treatment-for-joint-pain" element={<ConditionPage />} />
            <Route path="/yoga-naturopathy-for-stress" element={<ConditionPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
